import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDryRunRuleset } from '../hooks';
import { IRulesetDryRunResponse } from '../../../types/ruleset-types';
import { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';

interface DryRunPanelProps {
  dataPoolId: string;
  rulesetId?: string;
  disabled?: boolean;
}

const DryRunPanel: React.FC<DryRunPanelProps> = ({
  dataPoolId,
  rulesetId,
  disabled = false,
}) => {
  const [documentId, setDocumentId] = useState('');
  const [contextText, setContextText] = useState('');
  const [result, setResult] = useState<IRulesetDryRunResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const { mutate: dryRunRuleset, isPending } = useDryRunRuleset({
    onSuccess: (response) => {
      setErrorMessage('');
      setResult(response as IRulesetDryRunResponse);
    },
    onError: (error) => {
      setResult(null);
      setErrorMessage(error?.message || 'Dry run failed');
    },
  });

  const helperText = useMemo(() => {
    if (!rulesetId) {
      return 'Dry run becomes available after the ruleset is created.';
    }

    return 'Runs against the saved ruleset version. Provide a document id, context JSON, or both.';
  }, [rulesetId]);

  const handleRun = () => {
    if (!rulesetId) {
      return;
    }

    const trimmedDocumentId = documentId.trim();
    const trimmedContext = contextText.trim();

    let parsedContext: Record<string, unknown> | undefined;

    if (trimmedContext) {
      try {
        const parsed = JSON.parse(trimmedContext);
        if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
          setResult(null);
          setErrorMessage('Context JSON must be an object');
          return;
        }
        parsedContext = parsed as Record<string, unknown>;
      } catch {
        setResult(null);
        setErrorMessage('Context JSON is invalid');
        return;
      }
    }

    if (!trimmedDocumentId && !parsedContext) {
      setResult(null);
      setErrorMessage('Enter a document id, context JSON, or both');
      return;
    }

    dryRunRuleset({
      dataPoolId,
      rulesetId,
      data: {
        ...(trimmedDocumentId ? { document: trimmedDocumentId } : {}),
        ...(parsedContext ? { context: parsedContext } : {}),
      },
    });
  };

  return (
    <Accordion
      sx={{
        '&:before': { display: 'none' },
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        borderRadius: '4px !important',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: '#f5f5f5',
          minHeight: 40,
          '& .MuiAccordionSummary-content': { margin: '8px 0' },
        }}
      >
        <Typography variant="body2" fontWeight={500}>
          Dry Run
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>

          <FormControlStyled sx={{ mt: 0 }}>
            <FormLabelStyled>DOCUMENT ID</FormLabelStyled>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Optional"
              value={documentId}
              onChange={(e) => setDocumentId(e.target.value)}
              disabled={disabled || !rulesetId || isPending}
              fullWidth
              data-cy="input-ruleset-dry-run-document"
            />
          </FormControlStyled>

          <FormControlStyled sx={{ mt: 0 }}>
            <FormLabelStyled>CONTEXT OVERRIDES JSON</FormLabelStyled>
            <TextField
              size="small"
              variant="outlined"
              placeholder='{"status":"CREATED"}'
              value={contextText}
              onChange={(e) => setContextText(e.target.value)}
              disabled={disabled || !rulesetId || isPending}
              multiline
              minRows={4}
              fullWidth
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '12px' } }}
              data-cy="input-ruleset-dry-run-context"
            />
          </FormControlStyled>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              onClick={handleRun}
              disabled={disabled || !rulesetId || isPending}
              data-cy="btn-ruleset-dry-run"
              sx={{ textTransform: 'none' }}
            >
              {isPending ? 'Running...' : 'Run Dry Run'}
            </Button>
            {result && (
              <Chip
                label={result.matched ? 'Matched' : 'No Match'}
                color={result.matched ? 'success' : 'default'}
                size="small"
              />
            )}
          </Box>

          {errorMessage && (
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          )}

          {result && (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                fontSize: '12px',
                fontFamily: 'monospace',
                overflow: 'auto',
                maxHeight: 320,
                borderRadius: 1,
              }}
            >
              {JSON.stringify(result, null, 2)}
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default DryRunPanel;
